import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

export interface SearchableSelectOption {
  id: number | string;
  label: string;
  [key: string]: unknown;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: number | string | null;
  onChange: (value: number | string | null) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  searchPlaceholder?: string;
  getOptionLabel?: (option: SearchableSelectOption) => string;
  className?: string;
  disabled?: boolean;
}

// Normalizar texto para busca sem distinção de acentos ou maiúsculas/minúsculas
const normalizeSearchText = (text: unknown): string =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Selecione uma opção',
  label,
  required = false,
  searchPlaceholder = 'Buscar...',
  getOptionLabel = (option) => String(option?.label ?? ''),
  className = '',
  disabled = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  const safeOptions = Array.isArray(options) ? options : [];

  // Filtrar opções com suporte insensível a acentos (ex: "sao" encontra "São Paulo")
  const normalizedQuery = normalizeSearchText(searchQuery);
  const filteredOptions = safeOptions.filter((option) => {
    if (!option) return false;
    const optLabel = getOptionLabel ? getOptionLabel(option) : option.label;
    return normalizeSearchText(optLabel).includes(normalizedQuery);
  });

  // Encontrar a opção selecionada
  const selectedOption = safeOptions.find((opt) => opt && opt.id === value);

  // Fechar quando clicar fora APENAS em Desktop (no mobile usamos modal fullscreen com botão explícito de fechar)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        window.innerWidth >= 640 &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Travar rolagem do fundo no mobile quando o modal estiver aberto
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      if (window.innerWidth < 640) {
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Focar no input de busca ao abrir apenas no desktop (para evitar subir o teclado virtual no mobile)
  useLayoutEffect(() => {
    if (isOpen) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      if (!isMobile && searchInputRef.current) {
        try {
          searchInputRef.current.focus({ preventScroll: true });
        } catch {
          searchInputRef.current.focus();
        }
      }

      if (!isMobile && selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < 200 && spaceAbove > 240) {
          setDropUp(true);
        } else {
          setDropUp(false);
        }
      } else {
        setDropUp(false);
      }
    } else {
      setDropUp(false);
    }
  }, [isOpen]);

  const handleSelect = (optionId: number | string | null) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${isOpen ? 'z-50' : 'z-10'} ${className} notranslate`} ref={selectRef} translate="no">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3.5 py-3 sm:py-2 text-left bg-white border border-gray-300 rounded-xl sm:rounded-lg
          focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all
          flex items-center justify-between shadow-2xs min-h-[44px]
          ${!selectedOption ? 'text-gray-500' : 'text-gray-900 font-semibold'}
          ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : 'hover:border-emerald-300 active:bg-gray-50'}
        `}
      >
        <span className="truncate text-xs sm:text-sm notranslate" translate="no">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ml-2 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {/* DROPDOWN DESKTOP (telas sm em diante) */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className={`hidden sm:block absolute z-[9999] w-full bg-white border border-gray-300 rounded-xl shadow-xl max-h-64 overflow-hidden ${
            dropUp ? 'bottom-full mb-1' : 'mt-1 top-full'
          }`}
          style={{
            position: 'absolute',
            ...(dropUp ? { bottom: '100%', marginBottom: '4px' } : { top: '100%', marginTop: '4px' }),
          }}
        >
          {/* Campo de busca Desktop */}
          <div className="p-2 border-b border-gray-200 bg-gray-50/70">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-8 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                onClick={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Lista de opções Desktop */}
          <div className="overflow-y-auto max-h-48 divide-y divide-gray-50">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-xs text-gray-500 text-center">
                Nenhuma opção encontrada
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(null)}
                  className={`
                    w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-gray-100 transition-colors
                    ${!value ? 'bg-emerald-50 text-emerald-800 font-semibold' : 'text-gray-700'}
                  `}
                >
                  <span className="text-gray-500 italic">Nenhum</span>
                </button>
                {filteredOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(option.id)}
                    className={`
                      w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-emerald-50 active:bg-emerald-100 transition-colors
                      ${value === option.id ? 'bg-emerald-50 text-emerald-800 font-bold' : 'text-gray-700'}
                    `}
                  >
                    {getOptionLabel(option)}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL FULLSCREEN MOBILE (telas menores que sm) */}
      {isOpen && !disabled && typeof document !== 'undefined' && createPortal(
        <div className="sm:hidden fixed inset-0 z-[99999] bg-white flex flex-col animate-in fade-in duration-150 notranslate" translate="no">
          {/* Cabeçalho Mobile */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setSearchQuery('');
              }}
              className="p-1 -ml-1 text-gray-700 hover:text-gray-900 flex items-center gap-1 text-xs font-bold"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
              <span>Fechar</span>
            </button>
            <h3 className="text-sm font-bold text-gray-900 truncate max-w-[200px]">
              {label || placeholder}
            </h3>
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 p-1"
              >
                Limpar
              </button>
            ) : (
              <span className="text-xs text-gray-400 font-medium">
                {filteredOptions.length} opções
              </span>
            )}
          </div>

          {/* Campo de Busca Mobile */}
          <div className="p-3 border-b border-gray-100 bg-gray-50 shrink-0">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={mobileSearchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder || 'Buscar...'}
                className="w-full pl-10 pr-9 py-2.5 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-2xs"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Lista de Opções Mobile com Touch Targets confortáveis */}
          <div className="flex-1 overflow-y-auto overscroll-contain divide-y divide-gray-100 p-2 pb-16">
            {filteredOptions.length === 0 ? (
              <div className="py-12 text-center text-gray-500 space-y-2 px-4">
                <p className="text-sm font-semibold text-gray-800">
                  Nenhuma opção encontrada para &quot;{searchQuery}&quot;
                </p>
                <p className="text-xs text-gray-500">
                  Verifique se digitou corretamente ou tente buscar por partes da palavra.
                </p>
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="inline-block mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold rounded-xl"
                  >
                    Limpar termo de busca
                  </button>
                )}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleSelect(null)}
                  className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between rounded-xl transition-colors ${
                    !value
                      ? 'bg-emerald-50 text-emerald-800 font-bold'
                      : 'text-gray-500 active:bg-gray-100 italic'
                  }`}
                >
                  <span>Nenhum / Limpar seleção</span>
                  {!value && <CheckIcon className="w-5 h-5 text-emerald-600" />}
                </button>
                {filteredOptions.map((option) => {
                  const isSelected = value === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSelect(option.id)}
                      className={`w-full px-4 py-3.5 text-left text-sm flex items-center justify-between rounded-xl transition-colors ${
                        isSelected
                          ? 'bg-emerald-50 text-emerald-900 font-bold shadow-2xs'
                          : 'text-gray-800 active:bg-emerald-50/50'
                      }`}
                    >
                      <span className="truncate pr-2 font-medium notranslate" translate="no">{getOptionLabel(option)}</span>
                      {isSelected && <CheckIcon className="w-5 h-5 text-emerald-600 shrink-0" />}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

